import { DocumentNode, OperationVariables, QueryHookOptions } from '@apollo/client';
import { fetchCampaignFolderQuery } from 'api/queries';
import { maybeSplitFolderContents } from 'modules/domains/foldering';
import { GRAPHQL_TYPENAMES, GraphQLResponsePaginationInfo } from 'modules/graphql/types';
import { useGraphql } from 'utils/hooks/useGraphql';

import { GraphQLCampaign } from 'modules/campaignIndex/domainTypes';
import { GraphQLCampaignFolder, GraphQLCampaignSubfolder } from '../types';

export type CampaignFoldersResponse = {
  childFolders: GraphQLCampaignSubfolder[];
  childCampaigns: GraphQLCampaign[];
  error: boolean;
  folderData: GraphQLCampaignFolder;
  isLoading: boolean;
  paginationInfo: GraphQLResponsePaginationInfo;
  refetch: (variables: Partial<OperationVariables>) => void;
};

export enum ContentFilter {
  ContentItem = 'ContentItem',
  Subfolder = 'Subfolder',
}

type FetchFolderQueryResponse = {
  campaignFolder: {
    info: {
      count: number;
      limit: number;
      offset: number;
      page: number;
    };
    id: number;
    name: string;
    content: (GraphQLCampaignSubfolder | GraphQLCampaign)[];
    ancestors: (GraphQLCampaignSubfolder | GraphQLCampaign)[];
    __typename: GRAPHQL_TYPENAMES.CAMPAIGN_FOLDER;
  };
};

/**
 * A fn to
 * @param variables - variables to send to the API
 */
export function useCampaignFolders(
  options: QueryHookOptions,
  query: DocumentNode = fetchCampaignFolderQuery,
): CampaignFoldersResponse {
  const { data: folderQueryData, ...response } = useGraphql<FetchFolderQueryResponse>(query, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    ...options,
  });

  const { info = {}, ...folderData } = folderQueryData?.campaignFolder ?? {};

  const [folders, campaigns] = maybeSplitFolderContents<GraphQLCampaignSubfolder, GraphQLCampaign>(
    GRAPHQL_TYPENAMES.CAMPAIGN,
    folderQueryData?.campaignFolder?.content,
  );

  return {
    ...response,
    childFolders: folders,
    childCampaigns: campaigns,
    folderData: folderData as GraphQLCampaignFolder,
    paginationInfo: info as GraphQLResponsePaginationInfo,
  };
}
